import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, from, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { ExifItem, ExifToolResult } from './browser-exiftool.service';

@Injectable({
  providedIn: 'root',
})
export class ExiftoolApiService {
  // URL de la API de ExifTool (puede ser local o remota)
  private readonly apiUrl = 'https://exiftool.lucasgelfond.online/api/exif';

  constructor(private http: HttpClient) {}

  /**
   * Extrae metadatos de una imagen utilizando un servidor de ExifTool
   */
  public extractMetadata(file: File): Observable<ExifToolResult> {
    // Crear FormData para enviar el archivo
    const formData = new FormData();
    formData.append('file', file);

    // Intentar extraer metadatos localmente primero
    return from(this.extractMetadataLocally(file)).pipe(
      catchError((error) => {
        console.warn('Error extrayendo metadatos localmente, intentando API remota:', error);
        // Si falla, intentar con API remota
        return this.http.post<any>(this.apiUrl, formData).pipe(
          map((response) => this.mapResponseToExifToolResult(response)),
          catchError((err) => {
            console.error('Error al extraer metadatos con la API:', err);
            return of({
              metadata: [],
              hasManipulationSigns: false,
              manipulationIndicators: [],
              error: 'Error al procesar el archivo con ExifTool',
            });
          })
        );
      })
    );
  }

  /**
   * Intenta extraer metadatos localmente usando exifReader
   */
  private async extractMetadataLocally(file: File): Promise<ExifToolResult> {
    try {
      // Usar exifReader para obtener datos básicos
      const exifReader = require('exifreader');
      const tags = await exifReader.load(file);

      const metadata: ExifItem[] = [];

      // Convertir los datos de exifReader a nuestro formato
      for (const [key, value] of Object.entries(tags)) {
        if (value && typeof value === 'object' && 'description' in value) {
          metadata.push({
            label: key,
            value: String(value.description || ''),
          });
        }
      }

      // Añadir información del archivo
      metadata.push({ label: 'FileName', value: file.name });
      metadata.push({ label: 'FileSize', value: this.formatFileSize(file.size) });
      metadata.push({ label: 'FileType', value: file.type });
      metadata.push({ label: 'LastModified', value: new Date(file.lastModified).toLocaleString() });

      // Analizar para detectar manipulaciones
      const manipulationAnalysis = this.analyzeForManipulation(metadata);

      return {
        metadata,
        hasManipulationSigns: manipulationAnalysis.hasManipulationSigns,
        manipulationIndicators: manipulationAnalysis.indicators,
      };
    } catch (error) {
      console.error('Error al extraer metadatos localmente:', error);
      throw error;
    }
  }

  /**
   * Mapea la respuesta de la API a nuestro formato de ExifToolResult
   */
  private mapResponseToExifToolResult(response: any): ExifToolResult {
    try {
      if (!response || response.error) {
        return {
          metadata: [],
          hasManipulationSigns: false,
          manipulationIndicators: [],
          error: response?.error || 'Error desconocido',
        };
      }

      // Convertir el formato de la API al nuestro
      const metadata: ExifItem[] = Array.isArray(response.metadata)
        ? response.metadata
        : Object.entries(response).map(([key, value]) => ({
            label: key,
            value: String(value || ''),
          }));

      // Analizar para detectar manipulaciones
      const manipulationAnalysis = this.analyzeForManipulation(metadata);

      return {
        metadata,
        hasManipulationSigns: manipulationAnalysis.hasManipulationSigns,
        manipulationIndicators: manipulationAnalysis.indicators,
      };
    } catch (error) {
      console.error('Error al mapear la respuesta:', error);
      return {
        metadata: [],
        hasManipulationSigns: false,
        manipulationIndicators: [],
        error: 'Error al procesar la respuesta de ExifTool',
      };
    }
  }

  /**
   * Formatea el tamaño de archivo en KB/MB
   */
  private formatFileSize(bytes: number): string {
    if (bytes < 1024) {
      return bytes + ' bytes';
    } else if (bytes < 1024 * 1024) {
      return (bytes / 1024).toFixed(2) + ' KB';
    } else {
      return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
    }
  }

  /**
   * Analiza metadatos para detectar posibles manipulaciones
   */
  private analyzeForManipulation(metadata: ExifItem[]): { hasManipulationSigns: boolean; indicators: string[] } {
    const indicators: string[] = [];
    const suspiciousProperties = [
      'software',
      'photoshop',
      'edited',
      'modified',
      'creator',
      'ai',
      'neural',
      'lightroom',
      'gimp',
      'illustrator',
      'edit',
      'history',
      'modification',
    ];

    // Buscar propiedades sospechosas
    for (const item of metadata) {
      const label = item.label.toLowerCase();
      const value = item.value.toLowerCase();

      // Verificar si la propiedad o valor contienen palabras sospechosas
      const isSuspiciousLabel = suspiciousProperties.some((prop) => label.includes(prop));
      const isSuspiciousValue = suspiciousProperties.some((prop) => value.includes(prop));

      if (isSuspiciousLabel || isSuspiciousValue) {
        indicators.push(`Propiedad sospechosa: ${item.label}: ${item.value}`);
      }
    }

    // Verificar software usado
    const softwareItem = metadata.find((item) => item.label === 'Software');
    if (softwareItem) {
      const softwareLower = softwareItem.value.toLowerCase();
      const editingSoftware = [
        'photoshop',
        'lightroom',
        'gimp',
        'illustrator',
        'affinity',
        'capture',
      ];

      if (editingSoftware.some((s) => softwareLower.includes(s))) {
        indicators.push(`Software de edición detectado: ${softwareItem.value}`);
      }
    }

    // Verificar fechas
    const creationDate = metadata.find((item) =>
      item.label === 'CreateDate' || item.label === 'DateTimeOriginal'
    );
    const modifyDate = metadata.find((item) =>
      item.label === 'ModifyDate' || item.label === 'LastModified'
    );

    if (creationDate && modifyDate) {
      try {
        const createTime = new Date(creationDate.value).getTime();
        const modifyTime = new Date(modifyDate.value).getTime();

        if (!isNaN(createTime) && !isNaN(modifyTime)) {
          const diffMinutes = Math.abs(modifyTime - createTime) / (1000 * 60);

          if (diffMinutes > 5) {
            indicators.push(`Tiempo entre creación y modificación: ${Math.round(diffMinutes)} minutos`);
          }
        }
      } catch (e) {
        // Error al procesar fechas, ignorar
      }
    }

    // Verificar datos EXIF incompletos
    const hasMake = metadata.some((item) => item.label === 'Make');
    const hasModel = metadata.some((item) => item.label === 'Model');

    if (!hasMake || !hasModel) {
      indicators.push('Falta información del dispositivo (Make/Model)');
    }

    return {
      hasManipulationSigns: indicators.length > 0,
      indicators,
    };
  }
}
